import { FormEvent, useCallback, useEffect, useState } from 'react';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import { COLOR_RE, isLabelDto, parseItems, type LabelDto } from '../../lib/work-types';
import { ColorField, ColorSwatch, DEFAULT_COLOR } from './color-field';
import { useOverlays } from './overlay-context';
import { BrandEmpty, ErrorAlert, LoadingRows, PageTitle, WorkPageError } from './page-states';

type LabelForm = { name: string; color: string };

const EMPTY_FORM: LabelForm = { name: '', color: DEFAULT_COLOR };

export default function ProjectLabelsSettings({
  projectId,
  canManage,
}: {
  projectId: string;
  canManage: boolean;
}) {
  const { toast, confirm } = useOverlays();
  const [labels, setLabels] = useState<LabelDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LabelDto | null>(null);
  const [form, setForm] = useState<LabelForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);

  const labelsPath = v1Path(`/projects/${projectId}/labels`);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<unknown>(labelsPath);
      setLabels(parseItems(res, isLabelDto, 'labels'));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load labels'));
      setLabels([]);
    } finally {
      setLoading(false);
    }
  }, [labelsPath]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(label: LabelDto) {
    setEditing(label);
    setForm({ name: label.name, color: label.color });
    setFormError(null);
    setDialogOpen(true);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) {
      setFormError('Name is required.');
      return;
    }
    if (!COLOR_RE.test(form.color)) {
      setFormError('Colour must be a hex value like #60646C.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const body = JSON.stringify({ name, color: form.color });
      if (editing) {
        await apiFetch(`${labelsPath}/${editing.id}`, { method: 'PATCH', body });
        toast('Label updated');
      } else {
        await apiFetch(labelsPath, { method: 'POST', body });
        toast('Label created');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setFormError(apiErrorMessage(err, 'Failed to save label'));
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(label: LabelDto) {
    const ok = await confirm({
      title: 'Delete label',
      description: `Delete ${label.name}? It is removed from every work item that currently uses it.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setActingId(label.id);
    setError(null);
    try {
      await apiFetch(`${labelsPath}/${label.id}`, { method: 'DELETE' });
      toast('Label deleted');
      await load();
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to delete label');
      setError(message);
      toast(message);
    } finally {
      setActingId(null);
    }
  }

  const newLabelButton = (
    <Button type="button" size="sm" onClick={openCreate}>
      <Plus className="mr-1.5 h-4 w-4" />
      New label
    </Button>
  );

  return (
    <div className="flex flex-1 flex-col">
      <PageTitle
        title="Labels"
        count={labels.length}
        action={canManage ? newLabelButton : null}
      />
      <p className="mb-4 text-sm text-muted-foreground">
        Labels tag work items across states so you can filter the backlog and board by them.
      </p>
      <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />
      {loading ? (
        <LoadingRows columns={2} />
      ) : labels.length === 0 ? (
        <BrandEmpty title="No labels yet." />
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-0">Name</TableHead>
              <TableHead className="w-40">Colour</TableHead>
              {canManage ? <TableHead className="w-20 text-right">Actions</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {labels.map((label) => (
              <TableRow key={label.id}>
                <TableCell>
                  <span className="flex items-center gap-2 text-sm">
                    <ColorSwatch color={label.color} />
                    {label.name}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {label.color}
                </TableCell>
                {canManage ? (
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled={actingId === label.id}
                          aria-label={`Actions for ${label.name}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => openEdit(label)}>Edit</DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => void onDelete(label)}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit label' : 'New label'}</DialogTitle>
            <DialogDescription>
              Label names are unique within a stream.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            {formError ? <ErrorAlert>{formError}</ErrorAlert> : null}
            <div className="grid gap-2">
              <Label htmlFor="label-name">Name</Label>
              <Input
                id="label-name"
                value={form.name}
                maxLength={255}
                onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="label-color">Colour</Label>
              <ColorField
                id="label-color"
                value={form.color}
                onChange={(color) => setForm((f) => ({ ...f, color }))}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Save label' : 'Create label'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
